import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { motion } from 'framer-motion';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Chip from '@mui/material/Chip';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import VerifiedIcon from '@mui/icons-material/Verified';
import SpeedIcon from '@mui/icons-material/Speed';
import CalculateIcon from '@mui/icons-material/Calculate';
import Footer from '../components/Footer';
import './LandingPage.css';

const FEATURES = [
  {
    icon: <AutoFixHighIcon sx={{ fontSize: 40, color: '#5F5BCD' }} aria-hidden="true" />,
    title: 'AI-Powered Remediation',
    description:
      'Amazon Bedrock analyzes every page and applies accurate PDF/UA accessibility tags automatically — no manual effort required.',
  },
  {
    icon: <VerifiedIcon sx={{ fontSize: 40, color: '#5F5BCD' }} aria-hidden="true" />,
    title: 'PDF/UA Compliance',
    description:
      'Output PDFs meet PDF/UA and WCAG 2.1 standards, complete with an accessibility report for every job.',
  },
  {
    icon: <CloudUploadIcon sx={{ fontSize: 40, color: '#5F5BCD' }} aria-hidden="true" />,
    title: 'Simple Upload & Download',
    description:
      'Upload your PDF, sit back, and download the remediated document straight from the dashboard.',
  },
  {
    icon: <SpeedIcon sx={{ fontSize: 40, color: '#5F5BCD' }} aria-hidden="true" />,
    title: 'Real-Time Job Tracking',
    description:
      'Watch your jobs progress in real time — from upload through processing to download-ready.',
  },
];

export default function LandingPage() {
  const auth = useAuth();
  const navigate = useNavigate();

  const handleLogin = () => {
    auth.signinRedirect();
  };

  // If already authenticated, redirect straight to app
  React.useEffect(() => {
    if (auth.isAuthenticated) {
      navigate('/app');
    }
  }, [auth.isAuthenticated, navigate]);

  React.useEffect(() => {
    document.title = 'Remediation Analyzer — AI-Powered PDF Accessibility';
  }, []);

  return (
    <div className="landing-root">
      {/* ── NAV ─────────────────────────────────────────────── */}
      <nav className="landing-nav" aria-label="Main navigation">
        <div className="landing-nav-brand">
          <img
            src="/scale.png"
            alt="ScaleCapacity logo"
            style={{ height: '32px', width: 'auto', marginRight: '10px' }}
          />
        </div>
        <Button
          variant="outlined"
          onClick={handleLogin}
          sx={{
            color: '#fff',
            borderColor: 'rgba(255,255,255,0.5)',
            '&:hover': { borderColor: '#fff', backgroundColor: 'rgba(255,255,255,0.08)' },
          }}
        >
          Sign In
        </Button>
      </nav>

      {/* ── HERO ─────────────────────────────────────────────── */}
      <section id="main-content" className="landing-hero">
        <div className="landing-hero-bg" aria-hidden="true" />
        <motion.div
          className="landing-hero-content"
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
        >
          <Chip
            label="Powered by Amazon Bedrock"
            sx={{
              mb: 3,
              backgroundColor: 'rgba(84,251,245,0.15)',
              color: '#54FBF5',
              border: '1px solid rgba(84,251,245,0.4)',
              fontWeight: 600,
            }}
          />
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: '2rem', sm: '2.8rem', md: '3.5rem' },
              fontWeight: 900,
              color: '#fff',
              lineHeight: 1.15,
              mb: 2,
            }}
          >
            Make Your PDFs
            <br />
            <span className="landing-hero-gradient">Accessible by Default</span>
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: 'rgba(255,255,255,0.72)',
              fontWeight: 400,
              maxWidth: 560,
              mx: { xs: 'auto', md: 0 },
              mb: 4,
              lineHeight: 1.6,
            }}
          >
            Upload a PDF, get back a fully tagged, PDF/UA-compliant document in minutes — powered by AI.
          </Typography>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              backgroundColor: 'rgba(255, 167, 38, 0.18)',
              border: '1px solid rgba(255, 167, 38, 0.45)',
              borderRadius: '8px',
              px: 2,
              py: 0.75,
              mb: 4,
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: '#ffa726', fontWeight: 700, whiteSpace: 'nowrap' }}
            >
              New Regulations:
            </Typography>
            <Typography variant="body2" sx={{ color: '#fff', fontWeight: 500 }}>
              PDF/UA &amp; WCAG compliance deadline — April 2026
            </Typography>
          </Box>
          <Box sx={{ display: 'inline-flex', flexDirection: 'column', gap: 2, alignItems: 'stretch' }}>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                onClick={handleLogin}
                sx={{
                  backgroundColor: '#5F5BCD',
                  px: 4,
                  py: 1.5,
                  fontSize: '1rem',
                  '&:hover': { backgroundColor: '#4e4ab5' },
                }}
              >
                Get Started
              </Button>
              <Button
                variant="outlined"
                size="large"
                href="#features"
                sx={{
                  color: '#fff',
                  borderColor: 'rgba(255,255,255,0.4)',
                  px: 4,
                  py: 1.5,
                  fontSize: '1rem',
                  '&:hover': { borderColor: '#fff', backgroundColor: 'rgba(255,255,255,0.06)' },
                }}
              >
                Learn More
              </Button>
            </Box>

            {/* ── Cost Calculator CTA ─────────────────────────── */}
            <Button
              component="a"
              href="/PDFRemediationCostCalculator.html"
              target="_blank"
              rel="noopener noreferrer"
              variant="outlined"
              size="large"
              startIcon={<CalculateIcon aria-hidden="true" />}
              aria-label="Calculate Your Cost (opens in new tab)"
              sx={{
                color: '#54FBF5',
                borderColor: 'rgba(84,251,245,0.4)',
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 600,
                textTransform: 'none',
                '&:hover': {
                  borderColor: '#54FBF5',
                  backgroundColor: 'rgba(84,251,245,0.08)',
                },
              }}
            >
              Calculate Your Cost
            </Button>
          </Box>
        </motion.div>

        {/* Hero illustration */}
        <motion.div
          className="landing-hero-card"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7, delay: 0.2 }}
          aria-hidden="true"
        >
          <div className="hero-card-inner">
            <div className="hero-card-header">
              <span className="hero-card-dot red" />
              <span className="hero-card-dot yellow" />
              <span className="hero-card-dot green" />
              <span className="hero-card-title">remediation_analyzer</span>
            </div>
            <div className="hero-card-body">
              <div className="hero-card-step done">
                <span className="hero-card-step-icon">✓</span>
                <span>PDF uploaded</span>
              </div>
              <div className="hero-card-step done">
                <span className="hero-card-step-icon">✓</span>
                <span>Pages analyzed by AI</span>
              </div>
              <div className="hero-card-step active">
                <span className="hero-card-step-icon pulse" />
                <span>Applying accessibility tags…</span>
              </div>
              <div className="hero-card-step">
                <span className="hero-card-step-icon pending" />
                <span>Generating report</span>
              </div>
              <div className="hero-card-step">
                <span className="hero-card-step-icon pending" />
                <span>Ready to download</span>
              </div>
            </div>
            <div className="hero-card-footer">
              <span className="hero-card-badge">PDF/UA</span>
              <span className="hero-card-badge wcag">WCAG 2.1</span>
            </div>
          </div>
        </motion.div>
      </section>

      {/* ── FEATURES ─────────────────────────────────────────── */}
      <section id="features" className="landing-features">
        <Typography
          component="h2"
          variant="h4"
          align="center"
          sx={{ fontWeight: 800, mb: 1, color: '#0f0e2e' }}
        >
          Everything you need for PDF accessibility
        </Typography>
        <Typography
          align="center"
          sx={{ color: '#475569', mb: 6, maxWidth: 560, mx: 'auto' }}
        >
          From upload to compliant download — fully automated, enterprise-grade.
        </Typography>
        <Grid container spacing={4} justifyContent="center">
          {FEATURES.map((f, i) => (
            <Grid key={i} item xs={12} sm={6} md={3}>
              <motion.div
                className="feature-card"
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
              >
                <div className="feature-card-icon">{f.icon}</div>
                <Typography component="h3" variant="h6" sx={{ fontWeight: 700, mb: 1, color: '#0f0e2e' }}>
                  {f.title}
                </Typography>
                <Typography variant="body2" sx={{ color: '#475569', lineHeight: 1.7 }}>
                  {f.description}
                </Typography>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </section>

      {/* ── CTA ──────────────────────────────────────────────── */}
      <section className="landing-cta">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <Typography component="h2" variant="h4" sx={{ fontWeight: 800, color: '#fff', mb: 2 }}>
            Ready to remediate your first PDF?
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.7)', mb: 4, maxWidth: 480, mx: 'auto' }}>
            Sign in with your organisation account and start making documents accessible today.
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={handleLogin}
            sx={{
              backgroundColor: '#54FBF5',
              color: '#17134B',
              fontWeight: 700,
              px: 5,
              py: 1.5,
              fontSize: '1rem',
              '&:hover': { backgroundColor: '#3de8e2' },
            }}
          >
            Sign In to Get Started
          </Button>
        </motion.div>
      </section>

      {/* ── FOOTER ───────────────────────────────────────────── */}
      <Footer />
    </div>
  );
}
